/**
 * Oath Trend Service
 * API calls for agency trend data (YoY, Denomination, Historical) and executive summary
 *
 * Endpoints:
 *   GET  /oath/agency/{id}/trends          — cached trend data (yoy, denomination, historical)
 *   POST /oath/agency/{id}/trends/refresh  — force recompute and re-cache
 *   GET  /oath/agency/{id}/summary         — executive summary (KPI cards + narrative)
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

const parseResponse = async (response) => {
  const json = await response.json().catch(() => ({}));
  return json;
};

/**
 * Get cached trend data for a specific agency
 * @param {number} agencyId
 * @param {string} [include] - Comma-separated: yoy, denomination, historical. Defaults to all 3.
 * @returns {Promise<{success: boolean, data?: { yoy: Array, denomination: Array, historical: Array, lastUpdated: string }, cached?: boolean, error?: string}>}
 */
export const getAgencyTrends = async (agencyId, include) => {
  try {
    let url = `${BASE_URL}oath/agency/${agencyId}/trends`;
    if (include) {
      url += `?include=${encodeURIComponent(include)}`;
    }
    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get agency trends' };
    }

    return {
      success: result.success !== false,
      data: result.data,
      cached: result.cached,
    };
  } catch (error) {
    console.error('Get agency trends error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get consolidated trend data across all sub-agencies for a general agent
 * @param {number} agencyId - The general agent (parent) agency ID
 * @param {string} [include] - Comma-separated: yoy, denomination, historical. Defaults to all 3.
 * @returns {Promise<{success: boolean, data?: { yoy: Array, denomination: Array, historical: Array, lastUpdated: string }, cached?: boolean, error?: string}>}
 */
export const getConsolidatedTrends = async (agencyId, include) => {
  try {
    let url = `${BASE_URL}oath/agency/${agencyId}/trends/consolidated`;
    if (include) {
      url += `?include=${encodeURIComponent(include)}`;
    }
    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get consolidated trends' };
    }

    return {
      success: result.success !== false,
      data: result.data,
      cached: result.cached,
    };
  } catch (error) {
    console.error('Get consolidated trends error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Force recompute and re-cache trend data for a specific agency
 * @param {number} agencyId
 * @returns {Promise<{success: boolean, data?: object, refreshed?: boolean, error?: string}>}
 */
export const refreshAgencyTrends = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/agency/${agencyId}/trends/refresh`, {
      method: 'POST',
    });
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to refresh agency trends' };
    }

    return {
      success: result.success !== false,
      data: result.data,
      refreshed: result.refreshed,
    };
  } catch (error) {
    console.error('Refresh agency trends error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get executive summary for a specific agency
 * Pre-computed KPI data for summary tab narrative cards
 * @param {number} agencyId
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getAgencySummary = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/agency/${agencyId}/summary`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get agency summary' };
    }

    return {
      success: result.success !== false,
      data: result.data,
    };
  } catch (error) {
    console.error('Get agency summary error:', error);
    return { success: false, error: 'Network error' };
  }
};
